<template>
  <v-container class="px-4 py-8" fluid style="max-width: 1400px;">
    <!-- Header -->
    <div class="mb-8 text-center text-md-left">
      <h1 class="text-h3 font-weight-bold primary--text mb-2">
        <v-icon left size="40">mdi-help-circle</v-icon>
        {{ $t('ayuda') }}
      </h1>
      <p class="text-subtitle-1 grey--text text--darken-1">
        {{ getProfile && getProfile.email ? getProfile.email : $t('no_disponible') }}
      </p>
    </div>

    <!-- User Logged In Section -->
    <div v-if="getProfile && getProfile.email" class="mb-12">
      <!-- Tabs Navigation -->
      <v-card class="elevation-4 rounded-xl mb-6">
        <v-tabs v-model="activeTab" background-color="primary" dark slider-color="white" class="rounded-t-xl">
          <v-tab href="#tab-faq" class="font-weight-medium">
            <v-icon left>mdi-frequently-asked-questions</v-icon>
            {{ $t('preguntas_frecuentes') }}
          </v-tab>
          <v-tab href="#tab-tickets" class="font-weight-medium">
            <v-icon left>mdi-ticket-multiple</v-icon>
            {{ $t('mis_tickets') }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- FAQ Tab -->
          <v-tab-item value="tab-faq">
            <v-card-text class="pt-8">
              <!-- Search FAQ -->
              <v-row class="mb-2">
                <v-col cols="12" md="6" offset-md="3">
                  <v-text-field
                    v-model="faqSearch"
                    :label="$t('buscar_preguntas')"
                    prepend-inner-icon="mdi-magnify"
                    outlined rounded dense
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- FAQ Category Filter -->
              <v-row class="mb-4">
                <v-col cols="12">
                  <v-chip-group
                    v-model="selectedCategory"
                    mandatory
                    show-arrows
                    active-class="primary white--text"
                  >
                    <v-chip
                      v-for="cat in faqCategories"
                      :key="cat.value"
                      :value="cat.value"
                      filter
                    >
                      <v-icon left small>{{ cat.icon }}</v-icon>
                      {{ cat.label }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>

              <!-- FAQ Items -->
              <v-row>
                <v-col cols="12">
                  <div v-if="filteredFaqs.length > 0">
                    <v-expansion-panels v-model="expandedFaqs" multiple>
                      <v-expansion-panel
                        v-for="(faq, index) in filteredFaqs"
                        :key="index"
                        class="mb-3 rounded-lg"
                      >
                        <v-expansion-panel-header class="font-weight-medium">
                          <div>
                            <v-icon left small>mdi-help</v-icon>
                            {{ faq.question }}
                            <v-chip x-small outlined class="ml-2" color="primary">
                              <v-icon left x-small>{{ categoryIcon(faq.category) }}</v-icon>
                              {{ categoryLabel(faq.category) }}
                            </v-chip>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <p class="text-body2 grey--text text--darken-1">{{ faq.answer }}</p>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                  <div v-else class="text-center py-8">
                    <v-icon large color="grey" class="mb-2">mdi-folder-open-outline</v-icon>
                    <p class="grey--text">{{ $t('no_preguntas_encontradas') }}</p>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-tab-item>

          <!-- Tickets Tab -->
          <v-tab-item value="tab-tickets">

            <v-card-text class="pt-6">
              <v-btn
                color="primary"
                @click="openCreateDialog()"
                rounded large elevation="2" class="mb-6"
              >
                <v-icon left>mdi-plus</v-icon>
                {{ $t('nuevo_ticket') }}
              </v-btn>

              <v-data-table
                :headers="headers"
                :items="tickets"
                :loading="loadingTickets"
                :footer-props="{
                  itemsPerPageOptions: [5, 10, 25, 50],
                  itemsPerPageText: $t('filas_por_pagina')
                }"
                class="elevation-1 rounded-lg"
              >
                <template v-slot:item.status="{ item }">
                  <div class="d-flex align-center">
                    <v-chip :color="getStatusColor(item.status)" text-color="white" small>
                      {{ item.status }}
                    </v-chip>
                    <v-chip v-if="item.is_hardware && item.invoice_pdf_url" color="success" text-color="white" small class="ml-2 font-weight-bold">
                      <v-icon left small>mdi-receipt</v-icon> Factura
                    </v-chip>
                    <v-chip v-else-if="item.is_hardware && item.requires_invoice" color="amber darken-3" text-color="white" small class="ml-2">
                      <v-icon left small>mdi-clock-outline</v-icon> Factura Solicitada
                    </v-chip>
                  </div>
                </template>

                <template v-slot:item.created_date="{ item }">
                  {{ formatDate(item.created_date) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-icon small @click.stop="openDetailsDialog(item)" class="mr-2">
                    mdi-eye
                  </v-icon>
                </template>

                <template v-slot:no-data>
                  <div class="text-center py-8">
                    <v-icon large color="grey" class="mb-2">mdi-inbox-multiple</v-icon>
                    <p class="grey--text">{{ $t('no_hay_tickets') }}</p>
                  </div>
                </template>
              </v-data-table>

              <!-- Statistics Cards -->
              <v-row v-if="tickets.length > 0" class="mt-6">
                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-ticket</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold primary--text">{{ tickets.length }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('total_tickets') }}</p>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-alert-circle</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold" style="color: #FF9800;">{{ countByStatus('Nuevo') }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('nuevos') }}</p>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-progress-clock</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold" style="color: #2196F3;">{{ countByStatus('En progreso') }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('en_progreso') }}</p>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-check-circle</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold" style="color: #4CAF50;">{{ countByStatus('Resuelto') }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('resueltos') }}</p>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <!-- Create Ticket Dialog -->
      <v-dialog v-model="dialogCreateTicket" persistent scrollable max-width="600px">
        <v-card class="rounded-xl">
          <v-toolbar dark color="primary" flat>
            <v-toolbar-title class="font-weight-medium">
              <v-icon left>mdi-ticket-plus</v-icon>
              {{ $t('crear_ticket') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogCreateTicket = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-6">
            <v-form ref="formCreateTicket" @submit.prevent="submitCreateTicket()">
              <v-text-field
                v-model="newTicket.title"
                :label="$t('titulo')"
                prepend-inner-icon="mdi-pencil"
                :error-messages="errores.title"
                outlined rounded dense class="mb-4"
                required
              ></v-text-field>

              <v-textarea
                v-model="newTicket.description"
                :label="$t('descripcion')"
                prepend-icon="mdi-text"
                :error-messages="errores.description"
                outlined rounded dense class="mb-4"
                rows="4"
              ></v-textarea>

              <v-select
                v-model="newTicket.area_id"
                :items="areas"
                item-text="name"
                item-value="id"
                :label="$t('area')"
                prepend-inner-icon="mdi-folder-multiple"
                :error-messages="errores.area_id"
                outlined rounded dense class="mb-4"
              ></v-select>

              <v-text-field
                v-model="newTicket.client_name"
                :label="$t('nombre_cliente')"
                prepend-inner-icon="mdi-account"
                :error-messages="errores.client_name"
                outlined rounded dense class="mb-4"
                readonly
              ></v-text-field>

              <v-text-field
                v-model="newTicket.client_email"
                type="email"
                :label="$t('email')"
                prepend-inner-icon="mdi-email"
                :error-messages="errores.client_email"
                outlined rounded dense class="mb-4"
                readonly
              ></v-text-field>

              <v-text-field
                v-model="newTicket.client_phone"
                :label="$t('telefono')"
                prepend-inner-icon="mdi-phone"
                :error-messages="errores.client_phone"
                outlined rounded dense class="mb-6"
                readonly
              ></v-text-field>

              <v-alert v-if="errorAlert" type="error" outlined text class="mb-4">
                {{ errorAlert }}
              </v-alert>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn color="grey" @click="dialogCreateTicket = false" rounded>
              {{ $t('cancelar') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="submitCreateTicket()"
              :loading="loadingCreateTicket"
              rounded large elevation="2"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('crear') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <!-- ── MODAL PRINCIPAL: DETALLES COMPLETOS DEL TICKET ── -->
      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <v-dialog v-model="dialogTicketDetails" max-width="850px" scrollable>
        <v-card v-if="selectedTicket" class="ticket-detail-modal-card rounded-xl">
          <!-- Toolbar Superior -->
          <v-toolbar flat dark color="primary" class="ticket-modal-toolbar">
            <v-icon left>mdi-ticket-confirmation</v-icon>
            <v-toolbar-title class="font-weight-bold">
              Ticket #{{ selectedTicket.folio || selectedTicket.id }}
            </v-toolbar-title>
            <v-chip :color="getStatusColor(selectedTicket.status)" text-color="white" small class="ml-3 font-weight-bold">
              {{ selectedTicket.status }}
            </v-chip>
            <v-chip v-if="selectedTicket.is_hardware" color="teal darken-1" text-color="white" small class="ml-2 font-weight-bold">
              <v-icon left small>mdi-chip</v-icon>
              Hardware
            </v-chip>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogTicketDetails = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <!-- Cabecera / Hero del Ticket -->
          <div class="ticket-hero-banner pa-4">
            <h2 class="text-h5 font-weight-bold mb-2">{{ selectedTicket.title }}</h2>
            <div class="ticket-hero-meta">
              <span class="meta-item"><v-icon small class="mr-1">mdi-map-marker</v-icon> {{ selectedTicket.area || 'General' }}</span>
              <span class="meta-item" v-if="selectedTicket.client_company"><v-icon small class="mr-1">mdi-home-city</v-icon> <strong>{{ selectedTicket.client_company }}</strong></span>
              <span class="meta-item" v-if="selectedTicket.client_name"><v-icon small class="mr-1">mdi-account</v-icon> {{ selectedTicket.client_name }}</span>
              <span class="meta-item"><v-icon small class="mr-1">mdi-clock-outline</v-icon> {{ formatDate(selectedTicket.created_date) }}</span>
            </div>
          </div>

          <!-- Pestañas de Navegación del Ticket (SOLO si está vinculado a Hardware) -->
          <v-tabs
            v-if="selectedTicket.is_hardware"
            v-model="detailTab"
            dark
            background-color="primary"
            slider-color="secondary"
            class="detail-modal-tabs"
          >
            <v-tab href="#tab-info" class="font-weight-bold text-none tab-item-white">
              <v-icon left small>mdi-file-document-outline</v-icon>
              Información General
            </v-tab>
            <v-tab href="#tab-tech" class="font-weight-bold text-none tab-item-white">
              <v-icon left small>mdi-account-hard-hat</v-icon>
              Revisión Técnica
            </v-tab>
            <v-tab href="#tab-quote" class="font-weight-bold text-none tab-item-white">
              <v-icon left small>mdi-file-sign</v-icon>
              Cotización & Aprobación
              <v-badge v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.directorQuote && selectedTicket.hardware_metadata.directorQuote.clientStatus === 'pendiente_cliente'" color="warning" inline>
                <template v-slot:badge>1</template>
              </v-badge>
            </v-tab>
            <v-tab href="#tab-invoice" class="font-weight-bold text-none tab-item-white">
              <v-icon left small>mdi-receipt</v-icon>
              Facturación
              <v-badge v-if="selectedTicket.invoice_pdf_url" color="success" inline>
                <template v-slot:badge><v-icon small color="white">mdi-check</v-icon></template>
              </v-badge>
            </v-tab>
          </v-tabs>

          <!-- Contenido de las Pestañas -->
          <v-card-text style="max-height: 580px;" class="pa-4 bg-light-gray">
            <!-- Si es ticket de hardware: mostramos las pestañas -->
            <v-tabs-items v-if="selectedTicket.is_hardware" v-model="detailTab">

              <!-- ── PESTAÑA 1: INFORMACIÓN GENERAL & ACTIVO ── -->
              <v-tab-item value="tab-info">
                <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
                  <h4 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
                    <v-icon small color="primary" class="mr-1">mdi-clipboard-text</v-icon>
                    Descripción del Problema
                  </h4>
                  <p class="text-body-1 text-pre-wrap mb-0">{{ cleanTicketDescription(selectedTicket) }}</p>
                </v-card>

                <!-- Comentarios de Solución y Cierre (por debajo de la descripción) -->
                <v-card
                  v-if="isClosedTicket(selectedTicket) && getClosureData(selectedTicket)"
                  class="elevation-1 pa-4 mb-3 border-radius-8"
                  style="background: #f8fafc; border: 1px solid #e2e8f0;"
                >
                  <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                    <div class="d-flex align-center">
                      <v-icon color="green darken-2" class="mr-2" small>mdi-check-circle</v-icon>
                      <h4 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-0">
                        Solución y Comentarios de Cierre
                      </h4>
                    </div>
                    <small class="grey--text text--darken-1" v-if="getClosureData(selectedTicket).closedAt">
                      Cerrado el: <strong>{{ formatClosureDate(getClosureData(selectedTicket).closedAt) }}</strong>
                    </small>
                  </div>
                  <p class="text-body-1 text-pre-wrap mb-0 mt-2" style="color: #334155; line-height: 1.5;">
                    {{ getClosureData(selectedTicket).comments }}
                  </p>
                  <div v-if="getClosureData(selectedTicket).files && getClosureData(selectedTicket).files.length > 0" class="mt-3">
                    <small class="font-weight-bold grey--text text--darken-2 d-block mb-2">
                      <v-icon small class="mr-1">mdi-camera</v-icon>
                      Evidencias fotográficas / adjuntos de cierre ({{ getClosureData(selectedTicket).files.length }}):
                    </small>
                    <div class="evidence-grid">
                      <div
                        v-for="(fUrl, fIdx) in getClosureData(selectedTicket).files"
                        :key="fIdx"
                        class="evidence-thumb-wrap"
                        @click="isPdfUrl(fUrl) ? openPdf(fUrl, 'Evidencia_Cierre_' + (fIdx + 1)) : previewPhoto(formatFileUrl(fUrl))"
                        :title="isPdfUrl(fUrl) ? 'Abrir archivo PDF' : 'Ver fotografía ampliada'"
                      >
                        <img v-if="!isPdfUrl(fUrl)" :src="formatFileUrl(fUrl)" alt="Evidencia de cierre" class="evidence-thumb" />
                        <div v-else class="d-flex flex-column align-center justify-center fill-height pa-2 text-center" style="background: #f1f5f9; height: 100%;">
                          <v-icon color="grey darken-2" medium>mdi-file-document-outline</v-icon>
                          <span class="text-caption grey--text text--darken-2 mt-1 font-weight-bold">PDF {{ fIdx + 1 }}</span>
                        </div>
                        <div class="thumb-overlay">
                          <v-icon dark small>{{ isPdfUrl(fUrl) ? 'mdi-open-in-new' : 'mdi-magnify-plus' }}</v-icon>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card>

                <!-- Tarjeta de Hardware Asociado si aplica -->
                <v-card v-if="selectedTicket.is_hardware || (selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.equipmentName)" class="elevation-1 pa-4 mb-3 hw-card border-radius-8">
                  <div class="d-flex align-center justify-space-between mb-3">
                    <h4 class="text-subtitle-1 font-weight-bold teal--text text--darken-3 mb-0">
                      <v-icon small color="teal darken-3" class="mr-1">mdi-server</v-icon>
                      Activo de Hardware Vinculado
                    </h4>
                    <v-chip small outlined color="teal darken-2">
                      {{ selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.equipmentCode ? selectedTicket.hardware_metadata.equipmentCode : 'Activo Registrado' }}
                    </v-chip>
                  </div>

                  <v-row class="hw-spec-grid">
                    <v-col cols="12" sm="6" class="mb-2">
                      <small class="grey--text">Equipo / Dispositivo</small>
                      <p class="text-body-1 font-weight-bold mb-0">{{ (selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.equipmentName) || selectedTicket.title }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" class="mb-2">
                      <small class="grey--text">Residencial / Ubicación</small>
                      <p class="text-body-1 mb-0">{{ selectedTicket.client_company || (getProfile && getProfile.housing_unit_name) || 'Residencial' }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" class="mb-2" v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.lastMaintenanceDate">
                      <small class="grey--text">Último Mantenimiento</small>
                      <p class="text-body-2 mb-0">{{ selectedTicket.hardware_metadata.lastMaintenanceDate }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" class="mb-2" v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.nextMaintenanceDate">
                      <small class="grey--text">Próximo Servicio Programado</small>
                      <p class="text-body-2 font-weight-bold teal--text text--darken-2 mb-0">{{ selectedTicket.hardware_metadata.nextMaintenanceDate }}</p>
                    </v-col>
                  </v-row>

                  <!-- Estado de Póliza / Cargo -->
                  <div class="policy-notice-box mt-3 pa-3" :class="isAdditionalChargeRequired() ? 'with-charge' : 'no-charge'">
                    <div class="d-flex align-center">
                      <v-icon small :color="isAdditionalChargeRequired() ? 'orange darken-3' : 'green darken-2'" class="mr-2">
                        {{ isAdditionalChargeRequired() ? 'mdi-cash-alert' : 'mdi-check-decagram' }}
                      </v-icon>
                      <strong>
                        {{ isAdditionalChargeRequired() ? 'Aplica Cargo Adicional (Cotización Requerida)' : 'Servicio Cubierto por Póliza de Mantenimiento' }}
                      </strong>
                    </div>
                    <p class="text-caption grey--text text--darken-2 mb-0 mt-1">
                      {{ isAdditionalChargeRequired()
                        ? 'La revisión técnica en sitio determinó requerimiento de cotización adicional para refacciones o trabajos extraordinarios.'
                        : 'Servicio ordinario cubierto bajo la póliza y contrato de soporte del sistema.' }}
                    </p>
                  </div>
                </v-card>

                <!-- Tarjeta de Contacto & Asignación -->
                <v-card class="elevation-1 pa-4 border-radius-8">
                  <h4 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
                    <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
                    Detalles de Atención
                  </h4>
                  <v-row>
                    <v-col cols="12" sm="6" class="mb-3">
                      <small class="grey--text">Responsable Técnico</small>
                      <p class="text-body-1 mb-0">{{ selectedTicket.responsable || 'Sin asignar' }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" class="mb-3">
                      <small class="grey--text">Área Canalizada</small>
                      <p class="text-body-1 mb-0">{{ selectedTicket.area || 'General' }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" class="mb-3" v-if="selectedTicket.client_email">
                      <small class="grey--text">Correo del Solicitante</small>
                      <p class="text-body-2 mb-0">{{ selectedTicket.client_email }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" class="mb-3" v-if="selectedTicket.client_phone">
                      <small class="grey--text">Teléfono de Contacto</small>
                      <p class="text-body-2 mb-0">{{ selectedTicket.client_phone }}</p>
                    </v-col>
                  </v-row>
                </v-card>
              </v-tab-item>

              <!-- ── PESTAÑA 2: REVISIÓN TÉCNICA EN SITIO ── -->
              <v-tab-item value="tab-tech">
                <!-- Si existe Reporte Técnico -->
                <div v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.technicalReview">
                  <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
                    <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                      <div>
                        <h4 class="text-subtitle-1 font-weight-bold mb-0">Hoja de Revisión Técnica en Sitio</h4>
                        <small class="grey--text">Realizada el {{ selectedTicket.hardware_metadata.technicalReview.reviewDate }}</small>
                      </div>
                      <v-chip :color="getEquipmentConditionColor(selectedTicket.hardware_metadata.technicalReview.equipmentCondition)" text-color="white" small class="font-weight-bold">
                        Estado: {{ selectedTicket.hardware_metadata.technicalReview.equipmentCondition }}
                      </v-chip>
                    </div>

                    <!-- Diagnóstico y Hallazgos -->
                    <div class="findings-box pa-3 mb-3">
                      <small class="font-weight-bold teal--text text--darken-3">Diagnóstico y Pruebas Realizadas:</small>
                      <p class="text-body-1 text-pre-wrap mb-0 mt-1">{{ selectedTicket.hardware_metadata.technicalReview.findings }}</p>
                    </div>

                    <!-- Galería de Evidencias Fotográficas -->
                    <div v-if="selectedTicket.hardware_metadata.technicalReview.images && selectedTicket.hardware_metadata.technicalReview.images.length > 0" class="evidence-gallery-section mb-3">
                      <h5 class="text-body-2 font-weight-bold mb-2 grey--text text--darken-2">
                        <v-icon small class="mr-1">mdi-camera</v-icon>
                        Evidencias Fotográficas en Sitio ({{ selectedTicket.hardware_metadata.technicalReview.images.length }}):
                      </h5>
                      <div class="evidence-grid">
                        <div
                          v-for="(img, idx) in selectedTicket.hardware_metadata.technicalReview.images"
                          :key="idx"
                          class="evidence-thumb-wrap"
                          @click="previewPhoto(img)"
                        >
                          <img :src="img" alt="Evidencia en sitio" class="evidence-thumb" />
                          <div class="thumb-overlay">
                            <v-icon dark small>mdi-magnify-plus</v-icon>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Notas del Técnico -->
                    <div v-if="selectedTicket.hardware_metadata.technicalReview.technicianNotes" class="tech-notes-card pa-3">
                      <small class="font-weight-bold grey--text text--darken-3">Recomendaciones técnicas adicionales:</small>
                      <p class="text-body-2 grey--text text--darken-2 mb-0 mt-1">{{ selectedTicket.hardware_metadata.technicalReview.technicianNotes }}</p>
                    </div>
                  </v-card>
                </div>

                <!-- Si aún no hay reporte técnico -->
                <div v-else class="text-center py-6">
                  <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-clock-alert-outline</v-icon>
                  <h3 class="text-h6 font-weight-bold grey--text text--darken-2 mb-1">Revisión Técnica en Sitio Pendiente</h3>
                  <p class="text-body-2 grey--text mb-0" style="max-width: 460px; margin: 0 auto;">
                    El técnico asignado acudirá a las instalaciones para realizar la inspección física, pruebas operativas y capturar el diagnóstico correspondiente.
                  </p>
                </div>
              </v-tab-item>

              <!-- ── PESTAÑA 3: COTIZACIÓN & APROBACIÓN ── -->
              <v-tab-item value="tab-quote">
                <!-- Si existe Cotización del Director Técnico -->
                <div v-if="selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.directorQuote">
                  <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
                    <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                      <div>
                        <h4 class="text-subtitle-1 font-weight-bold mb-0">Cotización Oficial de Trabajo</h4>
                        <small class="grey--text">
                          Folio: <strong>{{ selectedTicket.hardware_metadata.directorQuote.quoteFolio }}</strong> ·
                          Vigencia: {{ selectedTicket.hardware_metadata.directorQuote.validityDays }} días
                        </small>
                      </div>
                      <!-- Chip de Estado de Aprobación -->
                      <div class="mt-2 mt-sm-0">
                        <v-chip
                          :color="getQuoteStatusColor(selectedTicket.hardware_metadata.directorQuote.clientStatus)"
                          text-color="white"
                          small
                          class="font-weight-bold"
                        >
                          <v-icon left small>{{ getQuoteStatusIcon(selectedTicket.hardware_metadata.directorQuote.clientStatus) }}</v-icon>
                          {{ getQuoteStatusLabel(selectedTicket.hardware_metadata.directorQuote.clientStatus) }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Tabla de Partidas -->
                    <div class="quote-table-wrapper mb-3">
                      <table class="quote-table">
                        <thead>
                          <tr>
                            <th>Concepto / Partida</th>
                            <th style="width: 15%; text-align: center;">Cantidad</th>
                            <th style="width: 20%; text-align: right;">P. Unitario</th>
                            <th style="width: 20%; text-align: right;">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, idx) in selectedTicket.hardware_metadata.directorQuote.items" :key="idx">
                            <td>{{ item.concept }}</td>
                            <td style="text-align: center;">{{ item.quantity }}</td>
                            <td style="text-align: right;">{{ formatCurrency(item.unitPrice) }}</td>
                            <td style="text-align: right;" class="font-weight-bold">{{ formatCurrency(item.total) }}</td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- Totales -->
                      <div class="quote-totals-card pa-3 mt-2">
                        <div class="d-flex justify-space-between mb-1">
                          <span class="grey--text">Subtotal:</span>
                          <strong>{{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.subtotal) }}</strong>
                        </div>
                        <div class="d-flex justify-space-between mb-1">
                          <span class="grey--text">IVA (16%):</span>
                          <strong>{{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.tax) }}</strong>
                        </div>
                        <v-divider class="my-2"></v-divider>
                        <div class="d-flex justify-space-between align-center">
                          <span class="text-subtitle-1 font-weight-bold">Total:</span>
                          <strong class="text-h6 primary--text font-weight-bold">{{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.total) }}</strong>
                        </div>
                      </div>
                    </div>

                    <!-- Documento PDF Oficial Adjunto -->
                    <div v-if="selectedTicket.hardware_metadata.directorQuote.pdfUrl" class="d-flex align-center justify-space-between flex-wrap pa-3 my-3 border-radius-8" style="background: #eef2ff; border: 1.5px solid #c7d2fe;">
                      <div class="d-flex align-center">
                        <v-icon color="red darken-2" class="mr-3" size="32">mdi-file-pdf-box</v-icon>
                        <div>
                          <strong class="indigo--text text--darken-4 d-block">Documento PDF Oficial de Cotización</strong>
                          <span class="text-caption grey--text text--darken-2">
                            {{ selectedTicket.hardware_metadata.directorQuote.pdfName || (selectedTicket.hardware_metadata.directorQuote.quoteFolio + '.pdf') }}
                          </span>
                        </div>
                      </div>
                      <v-btn
                        small
                        color="indigo darken-1"
                        dark
                        class="font-weight-bold text-none mt-2 mt-sm-0"
                        @click="openPdf(selectedTicket.hardware_metadata.directorQuote.pdfUrl, selectedTicket.hardware_metadata.directorQuote.pdfName || (selectedTicket.hardware_metadata.directorQuote.quoteFolio + '.pdf'))"
                      >
                        <v-icon left small>mdi-open-in-new</v-icon>
                        Ver / Descargar PDF
                      </v-btn>
                    </div>

                    <!-- Banner Factura Emitida si aplica -->
                    <div
                      v-if="selectedTicket.invoice_pdf_url"
                      class="pa-3 mt-3 border-radius-8"
                      style="background: #e8f5e9; border: 1.5px solid #81c784;"
                    >
                      <v-row align="center" justify="space-between">
                        <v-col cols="12" sm="8" class="py-1">
                          <div class="d-flex align-center">
                            <v-icon color="success" class="mr-2">mdi-receipt</v-icon>
                            <div>
                              <strong class="success--text text--darken-3 text-body-2">Comprobante Fiscal Digital (Factura) Disponible</strong>
                              <p class="text-caption grey--text text--darken-2 mb-0">{{ selectedTicket.invoice_pdf_name || ('Factura_' + (selectedTicket.folio || selectedTicket.id) + '.pdf') }}</p>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="4" class="text-sm-right py-1">
                          <v-btn
                            small
                            color="success"
                            dark
                            class="font-weight-bold text-none"
                            @click="openPdf(selectedTicket.invoice_pdf_url, selectedTicket.invoice_pdf_name || ('Factura_' + (selectedTicket.folio || selectedTicket.id) + '.pdf'))"
                          >
                            <v-icon left small>mdi-open-in-new</v-icon>
                            Ver Factura (PDF)
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Panel de Acción / Estado de Aprobación por la Residencia -->
                    <div class="quote-approval-card pa-4 mt-3" :class="selectedTicket.hardware_metadata.directorQuote.clientStatus">
                      <!-- 1. Si está pendiente de aprobación -->
                      <div v-if="selectedTicket.hardware_metadata.directorQuote.clientStatus === 'pendiente_cliente'">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="orange darken-2" class="mr-2">mdi-timer-sand</v-icon>
                          <h4 class="text-subtitle-1 font-weight-bold orange--text text--darken-3 mb-0">
                            Esperando Autorización del Cliente
                          </h4>
                        </div>
                        <p class="text-body-2 grey--text text--darken-2 mb-3">
                          Por favor revisa el presupuesto. Al autorizar la cotización, el equipo técnico será notificado inmediatamente para iniciar los trabajos.
                        </p>
                        <div class="d-flex flex-wrap" style="gap: 12px;">
                          <v-btn
                            color="success"
                            class="font-weight-bold text-none"
                            @click="openQuoteModal('aprobada')"
                          >
                            <v-icon left>mdi-check-circle</v-icon>
                            Aprobar Cotización
                          </v-btn>
                          <v-btn
                            color="error"
                            text
                            class="font-weight-bold text-none"
                            @click="openQuoteModal('rechazada')"
                          >
                            <v-icon left>mdi-close-circle</v-icon>
                            Rechazar
                          </v-btn>
                        </div>
                      </div>

                      <!-- 2. Si ya fue aprobada -->
                      <div v-else-if="selectedTicket.hardware_metadata.directorQuote.clientStatus === 'aprobada'">
                        <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                          <div class="d-flex align-center">
                            <v-icon color="success" class="mr-2">mdi-check-decagram</v-icon>
                            <h4 class="text-subtitle-1 font-weight-bold success--text mb-0">
                              Cotización Autorizada Exitosamente
                            </h4>
                          </div>
                          <v-btn
                            small
                            outlined
                            color="error"
                            class="font-weight-bold text-none mt-1 mt-sm-0"
                            @click="openQuoteModal('rechazada')"
                          >
                            <v-icon left small>mdi-close-circle</v-icon>
                            Cambiar a Rechazada
                          </v-btn>
                        </div>
                        <v-row class="mt-2">
                          <v-col cols="12" sm="4" class="mb-1" v-if="selectedTicket.hardware_metadata.directorQuote.clientApprovalDate">
                            <small class="grey--text">Fecha de Autorización</small>
                            <p class="text-body-2 mb-0 font-weight-bold">{{ selectedTicket.hardware_metadata.directorQuote.clientApprovalDate }}</p>
                          </v-col>
                          <v-col cols="12" sm="4" class="mb-1" v-if="selectedTicket.hardware_metadata.directorQuote.clientApprovalUser">
                            <small class="grey--text">Autorizó</small>
                            <p class="text-body-2 mb-0 font-weight-bold">{{ selectedTicket.hardware_metadata.directorQuote.clientApprovalUser }}</p>
                          </v-col>
                          <v-col cols="12" sm="4" class="mb-1" v-if="selectedTicket.hardware_metadata.directorQuote.clientApprovalFolio">
                            <small class="grey--text">Folio de Autorización</small>
                            <p class="text-body-2 mb-0 font-weight-bold">{{ selectedTicket.hardware_metadata.directorQuote.clientApprovalFolio }}</p>
                          </v-col>
                          <v-col cols="12" class="mt-2" v-if="selectedTicket.hardware_metadata.directorQuote.clientNotes">
                            <small class="grey--text">Comentarios:</small>
                            <p class="text-body-2 mb-0 font-italic">"{{ selectedTicket.hardware_metadata.directorQuote.clientNotes }}"</p>
                          </v-col>
                        </v-row>
                      </div>

                      <!-- 3. Si fue rechazada -->
                      <div v-else-if="selectedTicket.hardware_metadata.directorQuote.clientStatus === 'rechazada'">
                        <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                          <div class="d-flex align-center">
                            <v-icon color="error" class="mr-2">mdi-close-circle</v-icon>
                            <h4 class="text-subtitle-1 font-weight-bold error--text mb-0">
                              Cotización Declinada
                            </h4>
                          </div>
                          <v-btn
                            small
                            color="success"
                            class="font-weight-bold text-none mt-1 mt-sm-0"
                            @click="openQuoteModal('aprobada')"
                          >
                            <v-icon left small>mdi-check-circle</v-icon>
                            Aprobar Cotización
                          </v-btn>
                        </div>
                        <p class="text-body-2 grey--text text--darken-2 mb-0 mt-2">
                          La cotización fue declinada. Motivo / Comentarios:
                          <strong>"{{ selectedTicket.hardware_metadata.directorQuote.clientNotes || 'Sin comentarios' }}"</strong>
                        </p>
                      </div>
                    </div>

                    <!-- Firma Presencial de Autorización en Sitio (si existe) -->
                    <div
                      v-if="selectedTicket.hardware_metadata.directorQuote.signature"
                      class="pa-3 mt-3 border-radius-8"
                      style="background: #f0fdfa; border: 1.5px solid #99f6e4;"
                    >
                      <div class="d-flex align-center mb-2">
                        <v-icon color="teal darken-2" class="mr-2">mdi-draw</v-icon>
                        <h4 class="text-subtitle-1 font-weight-bold teal--text text--darken-3 mb-0">
                          Firma Presencial en Sitio (Conformidad del Cliente)
                        </h4>
                      </div>
                      <v-row align="center" class="mt-1">
                        <v-col cols="12" sm="5" class="pa-2">
                          <div
                            class="pa-2 white border-radius-8 text-center"
                            style="border: 1px solid #99f6e4; max-height: 120px; display: flex; align-items: center; justify-content: center;"
                          >
                            <img
                              :src="selectedTicket.hardware_metadata.directorQuote.signature.signatureImage"
                              alt="Firma del cliente"
                              style="max-width: 100%; max-height: 100px; object-fit: contain;"
                            />
                          </div>
                        </v-col>
                        <v-col cols="12" sm="7" class="pa-2">
                          <p class="text-body-2 mb-1">
                            <strong>Firmante:</strong> {{ selectedTicket.hardware_metadata.directorQuote.signature.signerName }}
                            <span v-if="selectedTicket.hardware_metadata.directorQuote.signature.signerRole" class="text-caption grey--text text--darken-2">
                              ({{ selectedTicket.hardware_metadata.directorQuote.signature.signerRole }})
                            </span>
                          </p>
                          <p class="text-body-2 mb-0">
                            <strong>Fecha y Hora:</strong> {{ selectedTicket.hardware_metadata.directorQuote.signature.signedAt }}
                          </p>
                        </v-col>
                      </v-row>
                    </div>
                  </v-card>
                </div>

                <!-- Si no hay cotización requerida -->
                <div v-else class="text-center py-6">
                  <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-file-percent-outline</v-icon>
                  <h3 class="text-h6 font-weight-bold grey--text text--darken-2 mb-1">Sin Cotización Pendiente</h3>
                  <p class="text-body-2 grey--text mb-0" style="max-width: 460px; margin: 0 auto;">
                    Este reporte técnico no requiere cargos extraordinarios o la cotización formal se encuentra en preparación por el Director Técnico.
                  </p>
                </div>
              </v-tab-item>

              <!-- ── PESTAÑA 4: ETAPA 3 - FACTURACIÓN ── -->
              <v-tab-item value="tab-invoice">
                <v-card class="elevation-1 pa-4 border-radius-8 invoice-section-card">
                  <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                    <div class="d-flex align-center">
                      <v-icon color="indigo darken-2" class="mr-2">mdi-receipt</v-icon>
                      <div>
                        <h4 class="text-subtitle-1 font-weight-bold indigo--text text--darken-3 mb-0">
                          Facturación
                        </h4>
                        <small class="grey--text">Comprobante fiscal digital (CFDI) y datos de facturación para este activo</small>
                      </div>
                    </div>
                    <div>
                      <v-chip v-if="selectedTicket.invoice_pdf_url" color="success" text-color="white" small class="font-weight-bold">
                        <v-icon left small>mdi-check-circle</v-icon> Factura Emitida
                      </v-chip>
                      <v-chip v-else-if="invoiceForm.requires_invoice" color="warning" text-color="white" small class="font-weight-bold">
                        <v-icon left small>mdi-clock-outline</v-icon> Factura Solicitada
                      </v-chip>
                      <v-chip v-else color="grey lighten-2" text-color="grey darken-2" small>
                        Sin Factura
                      </v-chip>
                    </div>
                  </div>

                  <!-- Banner si ya existe Factura PDF Oficial -->
                  <div v-if="selectedTicket.invoice_pdf_url" class="invoice-available-box pa-3 mb-3 border-radius-8">
                    <v-row align="center" justify="space-between">
                      <v-col cols="12" sm="8" class="py-1">
                        <div class="d-flex align-center">
                          <v-icon size="36" color="success" class="mr-3">mdi-file-pdf-box</v-icon>
                          <div>
                            <strong class="success--text text--darken-2 text-body-1">Factura Oficial Disponible</strong>
                            <p class="text-caption grey--text text--darken-2 mb-0">
                              {{ selectedTicket.invoice_pdf_name || ('Factura_' + (selectedTicket.folio || selectedTicket.id) + '.pdf') }}
                            </p>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="4" class="text-sm-right py-1">
                        <v-btn
                          color="success"
                          dark
                          class="text-none font-weight-bold"
                          @click="openPdf(selectedTicket.invoice_pdf_url, selectedTicket.invoice_pdf_name || ('Factura_' + (selectedTicket.folio || selectedTicket.id) + '.pdf'))"
                        >
                          <v-icon left small>mdi-open-in-new</v-icon>
                          Ver Factura (PDF)
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Pregunta: ¿Requiere factura? -->
                  <div class="pa-3 mb-3 border-radius-8" style="background: #f8fafc; border: 1px solid #e2e8f0;">
                    <v-row align="center" justify="space-between">
                      <v-col cols="12" sm="7" class="py-1">
                        <strong class="text-body-2">¿Requiere factura para este ticket?</strong>
                        <p class="text-caption grey--text text--darken-1 mb-0">
                          Indica si requieres comprobante fiscal digital para este servicio o folio.
                        </p>
                      </v-col>
                      <v-col cols="12" sm="5" class="text-sm-right py-1">
                        <v-btn-toggle v-model="invoiceForm.requires_invoice" mandatory>
                          <v-btn :value="false" small :class="!invoiceForm.requires_invoice ? 'grey lighten-2 grey--text text--darken-3 font-weight-bold' : ''">
                            No
                          </v-btn>
                          <v-btn :value="true" small :class="invoiceForm.requires_invoice ? 'primary white--text font-weight-bold' : ''">
                            <v-icon left small>mdi-check</v-icon> Sí, requiero
                          </v-btn>
                        </v-btn-toggle>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Formulario de Datos Fiscales si Requiere Factura -->
                  <div v-if="invoiceForm.requires_invoice" class="mt-3">
                    <p class="text-caption grey--text text--darken-2 mb-2 font-weight-bold">
                      <v-icon small color="indigo" class="mr-1">mdi-domain</v-icon>
                      Datos Fiscales del Contribuyente (Receptor)
                    </p>

                    <v-row>
                      <v-col cols="12" sm="4" class="py-1">
                        <v-text-field
                          v-model="invoiceForm.rfc"
                          label="RFC *"
                          placeholder="XAXX010101000"
                          maxlength="13"
                          outlined
                          dense
                          @input="invoiceForm.rfc = (invoiceForm.rfc || '').toUpperCase()"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="8" class="py-1">
                        <v-text-field
                          v-model="invoiceForm.razon_social"
                          label="Nombre o Razón Social *"
                          placeholder="Nombre fiscal exacto"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" sm="4" class="py-1">
                        <v-text-field
                          v-model="invoiceForm.codigo_postal"
                          label="Código Postal Fiscal *"
                          placeholder="Ej: 01000"
                          maxlength="5"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="8" class="py-1">
                        <v-select
                          v-model="invoiceForm.regimen_fiscal"
                          :items="regimenFiscalOptions"
                          label="Régimen Fiscal *"
                          outlined
                          dense
                        ></v-select>
                      </v-col>

                      <v-col cols="12" class="py-1">
                        <v-select
                          v-model="invoiceForm.uso_cfdi"
                          :items="usoCfdiOptions"
                          label="Uso de CFDI *"
                          outlined
                          dense
                        ></v-select>
                      </v-col>
                    </v-row>

                    <v-alert v-if="!selectedTicket.invoice_pdf_url" type="info" outlined dense text class="my-2 text-caption">
                      Al guardar tus datos fiscales, nuestro departamento contable generará el comprobante fiscal en PDF y lo adjuntará directamente a este ticket.
                    </v-alert>
                  </div>

                  <!-- Mensajes de feedback -->
                  <v-alert v-if="invoiceSuccessMessage" type="success" dense outlined text class="my-2">
                    {{ invoiceSuccessMessage }}
                  </v-alert>
                  <v-alert v-if="invoiceErrorMessage" type="error" dense outlined text class="my-2">
                    {{ invoiceErrorMessage }}
                  </v-alert>

                  <!-- Botón para Guardar Datos Fiscales -->
                  <div class="d-flex justify-end mt-2">
                    <v-btn
                      color="primary"
                      :loading="savingInvoice"
                      :disabled="savingInvoice"
                      @click="saveInvoiceData"
                      class="text-none font-weight-bold"
                    >
                      <v-icon left small>mdi-content-save</v-icon>
                      Guardar Preferencia y Datos Fiscales
                    </v-btn>
                  </div>
                </v-card>
              </v-tab-item>

            </v-tabs-items>

            <!-- Si NO es ticket de hardware: mostramos directamente la Información General -->
            <div v-else>
              <v-card class="elevation-1 pa-4 mb-3 border-radius-8">
                <h4 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
                  <v-icon small color="primary" class="mr-1">mdi-clipboard-text</v-icon>
                  Descripción del Problema
                </h4>
                <p class="text-body-1 text-pre-wrap mb-0">{{ cleanTicketDescription(selectedTicket) }}</p>
              </v-card>

              <!-- Comentarios de Solución y Cierre (por debajo de la descripción) -->
              <v-card
                v-if="isClosedTicket(selectedTicket) && getClosureData(selectedTicket)"
                class="elevation-1 pa-4 mb-3 border-radius-8"
                style="background: #f8fafc; border: 1px solid #e2e8f0;"
              >
                <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                  <div class="d-flex align-center">
                    <v-icon color="green darken-2" class="mr-2" small>mdi-check-circle</v-icon>
                    <h4 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-0">
                      Solución y Comentarios de Cierre
                    </h4>
                  </div>
                  <small class="grey--text text--darken-1" v-if="getClosureData(selectedTicket).closedAt">
                    Cerrado el: <strong>{{ formatClosureDate(getClosureData(selectedTicket).closedAt) }}</strong>
                  </small>
                </div>
                <p class="text-body-1 text-pre-wrap mb-0 mt-2" style="color: #334155; line-height: 1.5;">
                  {{ getClosureData(selectedTicket).comments }}
                </p>
                <div v-if="getClosureData(selectedTicket).files && getClosureData(selectedTicket).files.length > 0" class="mt-3">
                  <small class="font-weight-bold grey--text text--darken-2 d-block mb-2">
                    <v-icon small class="mr-1">mdi-camera</v-icon>
                    Evidencias fotográficas / adjuntos de cierre ({{ getClosureData(selectedTicket).files.length }}):
                  </small>
                  <div class="evidence-grid">
                    <div
                      v-for="(fUrl, fIdx) in getClosureData(selectedTicket).files"
                      :key="fIdx"
                      class="evidence-thumb-wrap"
                      @click="isPdfUrl(fUrl) ? openPdf(fUrl, 'Evidencia_Cierre_' + (fIdx + 1)) : previewPhoto(formatFileUrl(fUrl))"
                      :title="isPdfUrl(fUrl) ? 'Abrir archivo PDF' : 'Ver fotografía ampliada'"
                    >
                      <img v-if="!isPdfUrl(fUrl)" :src="formatFileUrl(fUrl)" alt="Evidencia de cierre" class="evidence-thumb" />
                      <div v-else class="d-flex flex-column align-center justify-center fill-height pa-2 text-center" style="background: #f1f5f9; height: 100%;">
                        <v-icon color="grey darken-2" medium>mdi-file-document-outline</v-icon>
                        <span class="text-caption grey--text text--darken-2 mt-1 font-weight-bold">PDF {{ fIdx + 1 }}</span>
                      </div>
                      <div class="thumb-overlay">
                        <v-icon dark small>{{ isPdfUrl(fUrl) ? 'mdi-open-in-new' : 'mdi-magnify-plus' }}</v-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card>

              <v-card class="elevation-1 pa-4 border-radius-8">
                <h4 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
                  <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
                  Detalles de Atención
                </h4>
                <v-row>
                  <v-col cols="12" sm="6" class="mb-3">
                    <small class="grey--text">Responsable Técnico</small>
                    <p class="text-body-1 mb-0">{{ selectedTicket.responsable || 'Sin asignar' }}</p>
                  </v-col>
                  <v-col cols="12" sm="6" class="mb-3">
                    <small class="grey--text">Área Canalizada</small>
                    <p class="text-body-1 mb-0">{{ selectedTicket.area || 'General' }}</p>
                  </v-col>
                  <v-col cols="12" sm="6" class="mb-3" v-if="selectedTicket.client_email">
                    <small class="grey--text">Correo del Solicitante</small>
                    <p class="text-body-2 mb-0">{{ selectedTicket.client_email }}</p>
                  </v-col>
                  <v-col cols="12" sm="6" class="mb-3" v-if="selectedTicket.client_phone">
                    <small class="grey--text">Teléfono de Contacto</small>
                    <p class="text-body-2 mb-0">{{ selectedTicket.client_phone }}</p>
                  </v-col>
                </v-row>
              </v-card>

            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="pa-3">
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialogTicketDetails = false" class="text-none font-weight-bold">
              Cerrar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <!-- ── MODAL: RESPONDER / AUTORIZAR COTIZACIÓN ── -->
      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <v-dialog v-model="quoteResponseDialog" max-width="500px">
        <v-card class="elevation-2 border-radius-8">
          <v-toolbar flat dark :color="quoteResponseType === 'aprobada' ? 'success' : 'error'">
            <v-icon left>{{ quoteResponseType === 'aprobada' ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
            <v-toolbar-title class="font-weight-bold">
              {{ quoteResponseType === 'aprobada' ? 'Autorizar Cotización' : 'Declinar Cotización' }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="quoteResponseDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pa-4">
            <p class="text-body-1 mb-3">
              {{ quoteResponseType === 'aprobada'
                ? '¿Confirmas la autorización de esta cotización?'
                : '¿Deseas declinar esta cotización técnica?' }}
            </p>

            <div v-if="selectedTicket && selectedTicket.hardware_metadata && selectedTicket.hardware_metadata.directorQuote" class="pa-3 mb-3 bg-light-gray border-radius-8">
              <div class="d-flex justify-space-between align-center">
                <span>Monto Total:</span>
                <strong class="text-h6 font-weight-bold" :class="quoteResponseType === 'aprobada' ? 'success--text' : 'error--text'">
                  {{ formatCurrency(selectedTicket.hardware_metadata.directorQuote.total) }}
                </strong>
              </div>
            </div>

            <v-textarea
              v-model="quoteResponseNotes"
              label="Comentarios u observaciones (opcional)"
              placeholder="Escribe notas adicionales o instrucciones para el equipo técnico..."
              rows="3"
              outlined
              dense
            ></v-textarea>
          </v-card-text>

          <v-card-actions class="pa-3">
            <v-spacer></v-spacer>
            <v-btn text @click="quoteResponseDialog = false" :disabled="submittingQuoteResponse">Cancelar</v-btn>
            <v-btn
              :color="quoteResponseType === 'aprobada' ? 'success' : 'error'"
              :loading="submittingQuoteResponse"
              class="font-weight-bold text-none"
              @click="confirmQuoteResponse"
            >
              {{ quoteResponseType === 'aprobada' ? 'Confirmar Autorización' : 'Confirmar Declinación' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <!-- ── MODAL: PREVIEW DE FOTOGRAFÍA DE EVIDENCIA ── -->
      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <v-dialog v-model="previewImageDialog" max-width="800px">
        <v-card>
          <v-card-text class="pa-2 text-center">
            <img :src="previewImageUrl" style="max-width: 100%; max-height: 80vh; object-fit: contain;" />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="previewImageDialog = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </div>

    <!-- Not Logged In Message -->
    <div v-else class="text-center py-12">
      <v-icon size="80" color="grey" class="mb-4">mdi-alert-outline</v-icon>
      <h2 class="text-h5 grey--text text--darken-1 mb-2">{{ $t('no_disponible') }}</h2>
      <p class="text-body2 grey--text">{{ $t('debes_iniciar_sesion') }}</p>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { ticketsService } from '@/services/ticketsService'

export default {
  name: 'Ayuda',
  data () {
    return {
      activeTab: 'tab-faq',
      faqSearch: '',
      selectedCategory: 'todas',
      expandedFaqs: [],
      faqCategories: [
        { value: 'todas', label: 'Todas', icon: 'mdi-view-grid-outline' },
        { value: 'cuenta', label: 'Cuenta y Plataforma', icon: 'mdi-account-circle-outline' },
        { value: 'tickets', label: 'Tickets de Soporte', icon: 'mdi-ticket-multiple' },
        { value: 'visitas', label: 'Visitas e Invitados', icon: 'mdi-account-group' },
        { value: 'paqueteria', label: 'Paquetería', icon: 'mdi-package-variant-closed' },
        { value: 'agua', label: 'Servicio de Agua', icon: 'mdi-water' },
        { value: 'cuentas', label: 'Estados de Cuenta', icon: 'mdi-cash-multiple' },
        { value: 'mascotas', label: 'Mascotas', icon: 'mdi-paw' },
        { value: 'casa_club', label: 'Casa Club', icon: 'mdi-pool' },
        { value: 'multas', label: 'Multas de Tránsito', icon: 'mdi-speedometer' },
        { value: 'almacenamiento', label: 'Almacenamiento', icon: 'mdi-database' },
        { value: 'avisos', label: 'Avisos y Directorio', icon: 'mdi-bulletin-board' }
      ],
      faqs: [
        // Cuenta y Plataforma
        {
          category: 'cuenta',
          question: '¿Cómo restablezco mi contraseña?',
          answer: 'Desde la pantalla de inicio de sesión, selecciona "¿Olvidaste tu contraseña?" e ingresa tu correo electrónico registrado. Recibirás un enlace para crear una nueva contraseña.'
        },
        {
          category: 'cuenta',
          question: '¿Qué es la verificación en dos pasos (2FA)?',
          answer: 'Es una capa adicional de seguridad que solicita un código cada vez que inicias sesión. Puedes activarla o desactivarla desde la sección de Configuración.'
        },
        {
          category: 'cuenta',
          question: '¿Cómo actualizo mis datos personales o fotografías de identificación?',
          answer: 'Ingresa a Configuración > Información Personal para editar tu nombre, teléfono, fotografías de identificación y demás datos de tu perfil.'
        },
        {
          category: 'cuenta',
          question: '¿Puedo cambiar mi residencia dentro de la plataforma?',
          answer: 'Sí, desde la opción "Cambiar Residencia" puedes solicitar la actualización cuando te mudes a otra vivienda dentro del fraccionamiento.'
        },
        // Tickets de Soporte
        {
          category: 'tickets',
          question: '¿Cómo puedo crear un ticket de soporte?',
          answer: 'Para crear un ticket, haz clic en la pestaña "Mis Tickets" y luego selecciona el botón "Nuevo Ticket". Completa el formulario con los detalles de tu solicitud y selecciona el área correspondiente.'
        },
        {
          category: 'tickets',
          question: '¿Cuál es el tiempo de respuesta promedio?',
          answer: 'Nos esforzamos por responder todos los tickets dentro de 24 a 48 horas hábiles. Los tickets marcados como urgentes reciben atención prioritaria.'
        },
        {
          category: 'tickets',
          question: '¿Puedo editar un ticket después de crearlo?',
          answer: 'Los tickets no pueden ser editados después de su creación. Si necesitas hacer cambios, puedes crear un nuevo ticket con la información actualizada o contactar al equipo de soporte.'
        },
        {
          category: 'tickets',
          question: '¿Qué significan los diferentes estados de un ticket?',
          answer: 'Los estados son: Nuevo (recién creado), En progreso (siendo atendido), Resuelto (solución proporcionada) y Cerrado (ticket finalizado).'
        },
        {
          category: 'tickets',
          question: '¿Cómo recibo notificaciones sobre mis tickets?',
          answer: 'Recibirás notificaciones por correo electrónico cuando tu ticket sea creado, cuando se realicen cambios en su estado, y cuando sea resuelto.'
        },
        {
          category: 'tickets',
          question: '¿Qué hacer si mi pregunta no está en las preguntas frecuentes?',
          answer: 'Si tu pregunta no está respondida aquí, puedes crear un ticket de soporte en la pestaña "Mis Tickets" y nuestro equipo te ayudará lo antes posible.'
        },
        // Visitas e Invitados
        {
          category: 'visitas',
          question: '¿Cómo genero una invitación para un visitante?',
          answer: 'Desde la sección de Invitaciones, captura los datos de tu visitante y genera un código QR o de acceso que podrás compartir con él para agilizar su ingreso.'
        },
        {
          category: 'visitas',
          question: '¿Cómo valida el vigilante el acceso de mis invitados?',
          answer: 'El personal de vigilancia escanea el código QR o ingresa el código de acceso en la caseta para registrar la entrada y salida del visitante.'
        },
        {
          category: 'visitas',
          question: '¿Puedo consultar el historial de mis visitas?',
          answer: 'Sí, en la sección de Histórico puedes revisar los accesos e invitaciones registrados anteriormente.'
        },
        // Paquetería
        {
          category: 'paqueteria',
          question: '¿Cómo me entero de que llegó un paquete?',
          answer: 'Recibirás una notificación cuando el vigilante registre la llegada de un paquete a tu nombre en la sección de Paquetería.'
        },
        {
          category: 'paqueteria',
          question: '¿Dónde recojo mis paquetes?',
          answer: 'Los paquetes se resguardan en la caseta de vigilancia hasta que los recojas; solo necesitas identificarte al retirarlos.'
        },
        // Servicio de Agua
        {
          category: 'agua',
          question: '¿Cómo reporto una falla en el servicio de agua?',
          answer: 'Desde la sección de Servicio de Agua puedes generar un reporte, o crear un ticket de soporte si necesitas seguimiento personalizado.'
        },
        {
          category: 'agua',
          question: '¿Dónde consulto el estatus de mi servicio de agua?',
          answer: 'En la sección de Servicio de Agua dentro de tu perfil puedes ver el estatus y los reportes relacionados con tu vivienda.'
        },
        // Estados de Cuenta
        {
          category: 'cuentas',
          question: '¿Dónde consulto mi estado de cuenta?',
          answer: 'En la sección de Estados de Cuenta puedes ver el detalle de tus cargos, pagos y saldo actual.'
        },
        {
          category: 'cuentas',
          question: '¿Con qué frecuencia se actualiza mi estado de cuenta?',
          answer: 'Se actualiza conforme la administración registra nuevos cargos y pagos, por lo que te recomendamos revisarlo periódicamente.'
        },
        // Mascotas
        {
          category: 'mascotas',
          question: '¿Cómo registro a mi mascota?',
          answer: 'En la sección de Mascotas puedes agregar el nombre, tipo y demás datos de tu mascota para mantener su registro actualizado ante la administración.'
        },
        // Casa Club
        {
          category: 'casa_club',
          question: '¿Cómo consulto información y amenidades de la Casa Club?',
          answer: 'En la sección de Casa Club puedes ver la información general, amenidades disponibles y solicitar accesos o invitaciones.'
        },
        {
          category: 'casa_club',
          question: '¿Puedo inscribirme a torneos de la Casa Club?',
          answer: 'Sí, revisa la sección de Torneos dentro de Casa Club para ver las convocatorias e inscribirte.'
        },
        // Multas de Tránsito
        {
          category: 'multas',
          question: '¿Cómo consulto si tengo una multa por exceso de velocidad?',
          answer: 'En la sección de Multas por Exceso de Velocidad podrás ver las infracciones registradas asociadas a tu vivienda.'
        },
        // Almacenamiento
        {
          category: 'almacenamiento',
          question: '¿Qué es el almacenamiento de la plataforma?',
          answer: 'Es el espacio destinado a guardar fotografías, documentos y evidencias generadas en la plataforma, como fotos de accesos, invitados y eventos.'
        },
        {
          category: 'almacenamiento',
          question: '¿Qué pasa si mi fraccionamiento se queda sin espacio de almacenamiento?',
          answer: 'El administrador puede solicitar almacenamiento adicional desde la tarjeta de Almacenamiento disponible en el inicio (Home).'
        },
        // Avisos y Directorio
        {
          category: 'avisos',
          question: '¿Dónde veo los avisos de la administración?',
          answer: 'En la sección de Avisos encontrarás comunicados, el Reglamento y el Aviso de Privacidad publicados por tu administrador.'
        },
        {
          category: 'avisos',
          question: '¿Cómo encuentro los datos de contacto de la administración o vecinos?',
          answer: 'Consulta el Directorio para ver la información de contacto disponible según tu rol dentro de la plataforma.'
        }
      ],
      tickets: [],
      areas: [],
      selectedTicket: null,
      dialogCreateTicket: false,
      dialogTicketDetails: false,
      detailTab: 'tab-info',
      quoteResponseDialog: false,
      quoteResponseType: 'aprobada',
      quoteResponseNotes: '',
      submittingQuoteResponse: false,
      previewImageDialog: false,
      previewImageUrl: '',
      loadingTickets: false,
      loadingCreateTicket: false,
      loadingAreas: false,
      errorAlert: '',
      errores: {},
      newTicket: {
        title: '',
        description: '',
        area_id: null,
        client_name: '',
        client_email: '',
        client_phone: ''
      },
      headers: [],
      regimenFiscalOptions: [
        { value: '601', text: '601 - General de Ley Personas Morales' },
        { value: '603', text: '603 - Personas Morales con Fines no Lucrativos' },
        { value: '605', text: '605 - Sueldos y Salarios e Ingresos Asimilados a Salarios' },
        { value: '606', text: '606 - Arrendamiento' },
        { value: '612', text: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
        { value: '616', text: '616 - Sin obligaciones fiscales' },
        { value: '621', text: '621 - Incorporación Fiscal' },
        { value: '625', text: '625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
        { value: '626', text: '626 - Régimen Simplificado de Confianza (RESICO)' }
      ],
      usoCfdiOptions: [
        { value: 'G01', text: 'G01 - Adquisición de mercancías' },
        { value: 'G02', text: 'G02 - Devoluciones, descuentos o bonificaciones' },
        { value: 'G03', text: 'G03 - Gastos en general' },
        { value: 'I01', text: 'I01 - Construcciones' },
        { value: 'I02', text: 'I02 - Mobiliario y equipo de oficina por inversiones' },
        { value: 'I04', text: 'I04 - Equipo de cómputo y accesorios' },
        { value: 'I08', text: 'I08 - Otra maquinaria y equipo' },
        { value: 'D01', text: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
        { value: 'S01', text: 'S01 - Sin efectos fiscales' },
        { value: 'CP01', text: 'CP01 - Pagos' }
      ],
      invoiceForm: {
        requires_invoice: false,
        rfc: '',
        razon_social: '',
        codigo_postal: '',
        regimen_fiscal: '',
        uso_cfdi: 'G03'
      },
      savingInvoice: false,
      invoiceSuccessMessage: '',
      invoiceErrorMessage: ''
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    filteredFaqs () {
      let result = this.faqs
      if (this.selectedCategory && this.selectedCategory !== 'todas') {
        result = result.filter(faq => faq.category === this.selectedCategory)
      }
      if (this.faqSearch) {
        const search = this.faqSearch.toLowerCase()
        result = result.filter(faq =>
          faq.question.toLowerCase().includes(search) ||
          faq.answer.toLowerCase().includes(search)
        )
      }
      return result
    }
  },
  watch: {
    getProfile: {
      handler (newProfile) {
        if (newProfile && newProfile.email) {
          this.loadAreas()
          this.loadTickets()
        }
      },
      immediate: true
    }
  },
  beforeMount () {
    this.headers = [
      {
        text: this.$t('id'),
        align: 'left',
        sortable: true,
        value: 'id'
      },
      {
        text: this.$t('titulo'),
        align: 'left',
        sortable: true,
        value: 'title'
      },
      {
        text: this.$t('cliente'),
        align: 'left',
        sortable: true,
        value: 'client_name'
      },
      {
        text: this.$t('area'),
        align: 'left',
        sortable: true,
        value: 'area'
      },
      {
        text: this.$t('estado'),
        align: 'center',
        sortable: true,
        value: 'status'
      },
      {
        text: this.$t('fecha_creacion'),
        align: 'left',
        sortable: true,
        value: 'created_date'
      },
      {
        text: this.$t('acciones'),
        align: 'center',
        sortable: false,
        value: 'actions'
      }
    ]
  },
  methods: {
    categoryLabel (value) {
      const cat = this.faqCategories.find(c => c.value === value)
      return cat ? cat.label : ''
    },

    categoryIcon (value) {
      const cat = this.faqCategories.find(c => c.value === value)
      return cat ? cat.icon : 'mdi-help'
    },

    async loadAreas () {
      this.loadingAreas = true
      try {
        const response = await ticketsService.getAreas()
        if (response.success) {
          const rawAreas = response.data || []
          const filtered = rawAreas.filter(area => {
            const name = (area.name || '').toLowerCase()
            const category = (area.category || area.type || '').toLowerCase()
            return name.includes('residencia') || category.includes('residencia')
          })
          this.areas = filtered.length > 0 ? filtered : rawAreas
        }
      } catch (error) {
        console.error('Error loading areas:', error)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al cargar las áreas')
      } finally {
        this.loadingAreas = false
      }
    },

    async loadTickets () {
      this.loadingTickets = true
      try {
        const response = await ticketsService.getTicketsByEmail(this.getProfile.email, 100)
        if (response.success) {
          this.tickets = response.data
        }
      } catch (error) {
        console.error('Error loading tickets:', error)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al cargar los tickets')
      } finally {
        this.loadingTickets = false
      }
    },

    openCreateDialog () {
      this.resetForm()
      // Auto-llenar datos del usuario logueado
      if (this.getProfile) {
        this.newTicket.client_name = `${this.getProfile.first_name || ''} ${this.getProfile.last_name || ''}`.trim()
        this.newTicket.client_email = this.getProfile.email || ''
        this.newTicket.client_phone = this.getProfile.celular || this.getProfile.cellNumber || this.getProfile.phone || ''
      }
      this.errorAlert = ''
      this.dialogCreateTicket = true
    },

    async openDetailsDialog (ticket) {
      if (!ticket || !ticket.id) {
        return
      }

      this.loadingTickets = true
      this.detailTab = 'tab-info'
      try {
        const response = await ticketsService.getTicketById(ticket.id)
        if (response && response.success && response.data) {
          this.selectedTicket = response.data
          this.initInvoiceForm(this.selectedTicket)
          this.dialogTicketDetails = true
        } else {
          this.selectedTicket = ticket
          this.initInvoiceForm(this.selectedTicket)
          this.dialogTicketDetails = true
        }
      } catch (error) {
        console.error('Error al obtener detalles:', error)
        this.selectedTicket = ticket
        this.initInvoiceForm(this.selectedTicket)
        this.dialogTicketDetails = true
      } finally {
        this.loadingTickets = false
      }
    },

    initInvoiceForm (ticket) {
      if (!ticket) return
      const fiscal = ticket.fiscal_data || {}
      const clientFiscal = ticket.client_fiscal_info || {}

      // Buscar datos fiscales en el historial de tickets del usuario si este ticket no los tiene
      let prevFiscal = null
      if (this.tickets && this.tickets.length) {
        for (const t of this.tickets) {
          if (t.fiscal_data && (t.fiscal_data.rfc || t.fiscal_data.razon_social)) {
            prevFiscal = t.fiscal_data
            break
          }
        }
      }

      const rfc = fiscal.rfc || clientFiscal.rfc || (prevFiscal && prevFiscal.rfc) || ''
      const razonSocial = fiscal.razon_social || fiscal.razonSocial || clientFiscal.razon_social || clientFiscal.razonSocial || (prevFiscal && (prevFiscal.razon_social || prevFiscal.razonSocial)) || ''
      const cp = fiscal.codigo_postal || fiscal.zip || clientFiscal.codigo_postal || clientFiscal.zip || (prevFiscal && (prevFiscal.codigo_postal || prevFiscal.zip)) || ''
      const regimen = fiscal.regimen_fiscal || fiscal.regimenFiscal || clientFiscal.regimen_fiscal || clientFiscal.regimenFiscal || (prevFiscal && (prevFiscal.regimen_fiscal || prevFiscal.regimenFiscal)) || ''
      const uso = fiscal.uso_cfdi || fiscal.usoCfdi || clientFiscal.uso_cfdi || clientFiscal.usoCfdi || (prevFiscal && (prevFiscal.uso_cfdi || prevFiscal.usoCfdi)) || 'G03'

      this.invoiceForm = {
        requires_invoice: ticket.requires_invoice === true,
        rfc: rfc,
        razon_social: razonSocial || ticket.client_company || '',
        codigo_postal: cp,
        regimen_fiscal: regimen,
        uso_cfdi: uso
      }
      this.invoiceSuccessMessage = ''
      this.invoiceErrorMessage = ''

      const email = ticket.client_email || (this.getProfile && this.getProfile.email)
      if (email) {
        ticketsService.getClientFiscalInfo(email).then(res => {
          if (res && res.success && res.data) {
            const d = res.data
            if (d.rfc && !this.invoiceForm.rfc) this.invoiceForm.rfc = d.rfc
            if (d.razon_social || d.razonSocial) {
              if (!this.invoiceForm.razon_social || this.invoiceForm.razon_social === ticket.client_company) {
                this.invoiceForm.razon_social = d.razon_social || d.razonSocial
              }
            }
            if ((d.codigo_postal || d.zip) && !this.invoiceForm.codigo_postal) {
              this.invoiceForm.codigo_postal = d.codigo_postal || d.zip
            }
            if ((d.regimen_fiscal || d.regimenFiscal) && !this.invoiceForm.regimen_fiscal) {
              this.invoiceForm.regimen_fiscal = d.regimen_fiscal || d.regimenFiscal
            }
            if ((d.uso_cfdi || d.usoCfdi) && (!this.invoiceForm.uso_cfdi || this.invoiceForm.uso_cfdi === 'G03')) {
              this.invoiceForm.uso_cfdi = d.uso_cfdi || d.usoCfdi
            }
          }
        }).catch(() => {})
      }
    },

    async saveInvoiceData () {
      if (!this.selectedTicket) return
      this.savingInvoice = true
      this.invoiceSuccessMessage = ''
      this.invoiceErrorMessage = ''

      try {
        const payload = {
          requires_invoice: this.invoiceForm.requires_invoice,
          fiscal_data: {
            rfc: (this.invoiceForm.rfc || '').toUpperCase().trim(),
            razon_social: (this.invoiceForm.razon_social || '').trim(),
            codigo_postal: (this.invoiceForm.codigo_postal || '').trim(),
            regimen_fiscal: this.invoiceForm.regimen_fiscal || '',
            uso_cfdi: this.invoiceForm.uso_cfdi || ''
          }
        }

        if (this.invoiceForm.requires_invoice && !payload.fiscal_data.rfc) {
          this.invoiceErrorMessage = 'El RFC es obligatorio si requieres factura.'
          this.savingInvoice = false
          return
        }

        const res = await ticketsService.updateTicketInvoice(this.selectedTicket.id, payload)
        if (res && res.success) {
          this.selectedTicket.requires_invoice = payload.requires_invoice
          this.selectedTicket.fiscal_data = payload.fiscal_data
          this.invoiceSuccessMessage = 'Información fiscal guardada exitosamente.'

          const idx = this.tickets.findIndex(t => t.id === this.selectedTicket.id)
          if (idx !== -1) {
            this.$set(this.tickets, idx, {
              ...this.tickets[idx],
              requires_invoice: payload.requires_invoice,
              fiscal_data: payload.fiscal_data
            })
          }
        } else {
          this.invoiceErrorMessage = (res && res.message) || 'Error al guardar la información fiscal.'
        }
      } catch (e) {
        console.error('Error saving invoice data:', e)
        this.invoiceErrorMessage = 'Ocurrió un error al guardar los datos fiscales.'
      } finally {
        this.savingInvoice = false
      }
    },

    openQuoteModal (action) {
      this.quoteResponseType = action
      this.quoteResponseNotes = ''
      this.quoteResponseDialog = true
    },

    async confirmQuoteResponse () {
      if (!this.selectedTicket) return
      this.submittingQuoteResponse = true
      const profile = this.getProfile || {}
      const userFullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username || 'Residente'
      const payload = {
        status: this.quoteResponseType,
        approved_by_user: userFullName,
        notes: this.quoteResponseNotes,
        system_origin: 'Residencial'
      }
      try {
        const res = await ticketsService.respondQuote(this.selectedTicket.id, payload)
        if (res && res.success) {
          this.quoteResponseDialog = false
          this.selectedTicket = res.data
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Respuesta de cotización guardada exitosamente')
          this.$store.commit('snackbar_change_color', 'success')
          await this.loadTickets()
        }
      } catch (err) {
        console.error('Error respondiendo cotización:', err)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al procesar respuesta de cotización')
        this.$store.commit('snackbar_change_color', 'error')
      } finally {
        this.submittingQuoteResponse = false
      }
    },

    formatFileUrl (url) {
      if (!url) return ''
      if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
        return url
      }
      const baseUrl = process.env.VUE_APP_ODOO_API_URL || process.env.VUE_APP_TICKETS_API_URL || 'http://localhost:8069'
      const cleanBase = baseUrl.replace(/\/$/, '')
      const cleanPath = url.startsWith('/') ? url : `/${url}`
      return `${cleanBase}${cleanPath}`
    },

    isPdfUrl (url) {
      if (!url) return false
      const lower = String(url).toLowerCase()
      return lower.includes('application/pdf') || lower.endsWith('.pdf') || lower.includes('/web/content/')
    },

    previewPhoto (url) {
      this.previewImageUrl = url
      this.previewImageDialog = true
    },

    openPdf (pdfUrl, filename = 'cotizacion.pdf') {
      if (!pdfUrl) return
      if (pdfUrl.startsWith('data:application/pdf;base64,')) {
        try {
          const base64Data = pdfUrl.split(',')[1]
          const binaryString = atob(base64Data)
          const len = binaryString.length
          const bytes = new Uint8Array(len)
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          const blob = new Blob([bytes], { type: 'application/pdf' })
          const blobUrl = URL.createObjectURL(blob)
          const win = window.open(blobUrl, '_blank')
          if (!win) {
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
        } catch (e) {
          console.error('Error abriendo PDF con Blob:', e)
          window.open(pdfUrl, '_blank')
        }
      } else {
        window.open(pdfUrl, '_blank')
      }
    },

    cleanTicketDescription (ticket) {
      if (!ticket) return ''
      let text = ticket.clean_description || this.cleanHtml(ticket.description) || ''
      text = text.replace(/✓\s*Solución y Comentarios de Cierre[\s\S]*/i, '')
      text = text.replace(/Solución y Comentarios de Cierre[\s\S]*/i, '')
      return text.trim()
    },

    isClosedTicket (ticket) {
      if (!ticket) return false
      const st = (ticket.status || '').toLowerCase()
      return st === 'cerrado' || st === 'resuelto' || st === 'done' || st === 'finalizado'
    },

    cleanHtml (html) {
      if (!html) return '—'
      const sanitized = String(html)
        .replace(/<div class="(?:ticket-meta|hardware-ticket-meta|invoice-ticket-meta|ticket-closure-meta|ticket-closure-public)"[\s\S]*?<\/div>/gi, '')
      const tmp = document.createElement('div')
      tmp.innerHTML = sanitized
      return tmp.textContent || tmp.innerText || '—'
    },

    getClosureData (ticket) {
      if (!ticket) return null
      // 1. Si viene en closure_metadata (del API de Odoo)
      if (ticket.closure_metadata && ticket.closure_metadata.comments) {
        return ticket.closure_metadata
      }
      // 2. Si viene embebido en description (data-closure-json)
      const desc = ticket.description || ''
      const match = desc.match(/data-closure-json=(?:"|&#34;|&quot;)(.*?)(?:"|&#34;|&quot;)/i) || desc.match(/data-closure-json="([^"]*)"/i)
      if (match && match[1]) {
        try {
          const parsed = JSON.parse(decodeURIComponent(match[1]))
          if (parsed && parsed.comments) return parsed
        } catch (e) {}
      }
      // 3. Si viene en hardware_metadata.execution
      if (ticket.hardware_metadata && ticket.hardware_metadata.execution && ticket.hardware_metadata.execution.completionNotes) {
        return {
          comments: ticket.hardware_metadata.execution.completionNotes,
          closedBy: ticket.hardware_metadata.execution.closedBy || '',
          closedAt: ticket.hardware_metadata.execution.finishedDate || '',
          files: ticket.hardware_metadata.execution.finalImages || []
        }
      }
      return null
    },

    formatClosureDate (dateStr) {
      if (!dateStr) return ''
      try {
        const d = new Date(dateStr)
        if (isNaN(d.getTime())) return dateStr
        return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
      } catch (e) {
        return dateStr
      }
    },

    formatCurrency (val) {
      const num = parseFloat(val) || 0
      return '$' + num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MXN'
    },

    getEquipmentConditionColor (condition) {
      const map = {
        'excelente': '#4CAF50',
        'bueno': '#8BC34A',
        'regular': '#FF9800',
        'critico': '#F44336',
        'fuera_servicio': '#D32F2F'
      }
      return map[condition] || '#2196F3'
    },

    getQuoteStatusColor (status) {
      const map = {
        'pendiente_cliente': '#FF9800',
        'aprobada': '#4CAF50',
        'rechazada': '#F44336'
      }
      return map[status] || '#757575'
    },

    getQuoteStatusIcon (status) {
      const map = {
        'pendiente_cliente': 'mdi-timer-sand',
        'aprobada': 'mdi-check-decagram',
        'rechazada': 'mdi-close-circle'
      }
      return map[status] || 'mdi-help-circle'
    },

    getQuoteStatusLabel (status) {
      const map = {
        'pendiente_cliente': 'Pendiente de Autorización',
        'aprobada': 'Cotización Autorizada',
        'rechazada': 'Cotización Declinada'
      }
      return map[status] || status
    },

    isAdditionalChargeRequired () {
      if (!this.selectedTicket || !this.selectedTicket.hardware_metadata) return false
      const meta = this.selectedTicket.hardware_metadata
      if (meta.technicalReview && meta.technicalReview.needsQuote) return true
      if (meta.hasAdditionalCharge) return true
      if (meta.directorQuote) return true
      return false
    },

    async submitCreateTicket () {
      this.errores = {}
      this.errorAlert = ''

      if (!this.newTicket.title || !this.newTicket.title.trim()) {
        this.errores.title = ['El título es requerido']
        return
      }

      this.loadingCreateTicket = true
      try {
        const profile = this.getProfile || {}
        const residenceName = profile.housing_unit_name || profile.short_name || ''

        const ticketData = {
          title: this.newTicket.title,
          description: this.newTicket.description || '',
          area_id: this.newTicket.area_id || null,
          client_name: this.newTicket.client_name || '',
          client_email: this.newTicket.client_email || '',
          client_phone: this.newTicket.client_phone || '',
          client_company: residenceName
        }

        const response = await ticketsService.createTicket(ticketData)

        if (response.success) {
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Ticket creado exitosamente')
          this.$store.commit('snackbar_change_color', 'success')
          this.dialogCreateTicket = false
          this.resetForm()
          await this.loadTickets()
        }
      } catch (error) {
        console.error('Error creating ticket:', error)
        if (error.response && error.response.status === 400 && error.response.data) {
          const data = error.response.data
          if (data.error) {
            this.errorAlert = data.message || data.error
          } else {
            this.errores = data
          }
        } else {
          this.errorAlert = 'Error al crear el ticket. Por favor, intenta nuevamente.'
        }
      } finally {
        this.loadingCreateTicket = false
      }
    },

    resetForm () {
      this.newTicket = {
        title: '',
        description: '',
        area_id: null,
        client_name: '',
        client_email: '',
        client_phone: ''
      }
      this.errores = {}
      this.errorAlert = ''
    },

    getStatusColor (status) {
      const colorMap = {
        'Nuevo': '#FF9800',
        'En progreso': '#2196F3',
        'Resuelto': '#4CAF50',
        'Cerrado': '#9E9E9E'
      }
      return colorMap[status] || '#757575'
    },

    countByStatus (status) {
      return this.tickets.filter(ticket => ticket.status === status).length
    },

    formatDate (dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.v-data-table {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

/* ── Rich Ticket Detail Modal Styles ── */
.ticket-detail-modal-card {
  border-radius: 12px;
  overflow: hidden;
}

.ticket-modal-toolbar {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.ticket-hero-banner {
  background: linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%);
  border-bottom: 1px solid #bbdefb;
}

.ticket-hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #546e7a;
  font-size: 0.9rem;
}

.meta-item {
  display: inline-flex;
  align-items: center;
}

.detail-modal-tabs {
  background: #1976d2 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.detail-modal-tabs ::v-deep .v-tabs-bar {
  background: #1976d2 !important;
}

.detail-modal-tabs ::v-deep .v-tab,
.tab-item-white {
  color: #ffffff !important;
  font-size: 0.92rem !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  opacity: 0.9 !important;
  transition: all 0.2s ease;
}

.detail-modal-tabs ::v-deep .v-tab:hover,
.detail-modal-tabs ::v-deep .v-tab--active {
  color: #ffffff !important;
  opacity: 1 !important;
  font-weight: 700 !important;
}

.detail-modal-tabs ::v-deep .v-tab .v-icon,
.tab-item-white .v-icon {
  color: #ffffff !important;
}

.bg-light-gray {
  background-color: #f8fafc;
}

.border-radius-8 {
  border-radius: 8px !important;
}

.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

/* Hardware Card */
.hw-card {
  border: 1px solid #ccfbf1;
  background: #f0fdfa !important;
}

.policy-notice-box {
  border-radius: 6px;
}

.policy-notice-box.with-charge {
  background: #fff7ed;
  border: 1px solid #ffedd5;
}

.policy-notice-box.no-charge {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
}

/* Technical Review Section */
.findings-box {
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.evidence-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 12px;
  margin-top: 8px;
}

.evidence-thumb-wrap {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.evidence-thumb-wrap:hover {
  transform: scale(1.04);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border-color: #0f766e;
}

.evidence-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.evidence-thumb-wrap:hover .thumb-overlay {
  opacity: 1;
}

.tech-notes-card {
  background: #fafafa;
  border: 1px dashed #cfd8dc;
  border-radius: 6px;
}

/* Quote Section */
.quote-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.quote-table {
  width: 100%;
  border-collapse: collapse;
}

.quote-table th {
  background: #f1f5f9;
  color: #475569;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid #cbd5e1;
}

.quote-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.9rem;
}

.quote-totals-card {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.quote-approval-card {
  border-radius: 8px;
}

.quote-approval-card.pendiente_cliente {
  background: #fffbeb;
  border: 1px solid #fef3c7;
}

.quote-approval-card.aprobada {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
}

.quote-approval-card.rechazada {
  background: #fef2f2;
  border: 1px solid #fee2e2;
}

.invoice-section-card {
  border: 1px solid #e0e7ff !important;
}

.invoice-available-box {
  background: #f0fdf4;
  border: 1.5px solid #86efac;
}
</style>
